import { API_ENDPOINT, KUFER_URL, SAMMELN_URL } from '@xver/constants/env';
import {
    createNetworkError,
    showConnectionErrorNotification,
    showMaintenanceNotification,
} from '@xver/libs/connectionNotifier';
import store from '@xver/store';
import { setMaintenance } from '@xver/store/maintenance/maintenance.actions';
import { selectActiveProjectId } from '@xver/store/project/project.reducer';
import { getErrorCode, isSessionRejected } from '@xver/utils/error';
import { decodeJwtPayload } from '@xver/utils/jwt';
import storage from '@xver/utils/storage';
import axios, { AxiosError, AxiosInstance } from 'axios';

type InterceptorMode = 'flat' | 'envelope' | 'envelope-with-user-header';

/**
 * Sent with the active project on project-aware clients. A request that sets it itself keeps its
 * value; `''` asks the server for the caller's own default project.
 */
export const PROJECT_HEADER = 'X-Project-Id';

let projectErrorHandler: (() => void) | undefined;

/**
 * Registers the callback run when a request fails because the active project is gone
 * (`project_not_found` on a request that sent `X-Project-Id`) or the caller's project access
 * changed (`project_access_denied`). Pass undefined to unregister.
 */
export function setProjectErrorHandler(handler: (() => void) | undefined) {
    projectErrorHandler = handler;
}

let sessionExpiredHandler: (() => void) | undefined;

/**
 * Registers the callback run when kufer or sammeln rejects the session token: kufer's 401
 * `unauthenticated`, or the gateway's plain-text 401 for a missing, invalid or expired JWT. A
 * 401 `github_token_invalid` is not a session problem and never triggers it. It can fire for a
 * session xver-api still accepts (kufer unable to fetch the JWKS), so the handler must confirm
 * with xver-api before signing out. Pass undefined to unregister.
 */
export function setSessionExpiredHandler(handler: (() => void) | undefined) {
    sessionExpiredHandler = handler;
}

let lastDecodedToken = '';
let lastUserId: string | null = null;

function extractUserId(token: string): string | null {
    if (!token) return null;
    if (token === lastDecodedToken) return lastUserId;
    lastDecodedToken = token;
    lastUserId = null;

    const claims = decodeJwtPayload(token);
    if (claims?.userID === undefined || claims?.userID === null) return null;
    lastUserId = String(claims.userID);
    return lastUserId;
}

function attachInterceptors(
    client: AxiosInstance,
    mode: InterceptorMode = 'flat',
    withProjectHeader = false
) {
    client.interceptors.request.use((config) => {
        config.headers ??= {};
        const token = storage.get<string>('_zugang', '');

        if (withProjectHeader && !(PROJECT_HEADER in config.headers)) {
            const projectId = selectActiveProjectId(store.getState());
            if (projectId) {
                config.headers[PROJECT_HEADER] = projectId;
            }
        }

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
            if (mode === 'envelope-with-user-header') {
                const uid = extractUserId(token);
                if (uid) {
                    config.headers['X-User-Id'] = uid;
                }
            }
        }

        return config;
    });

    client.interceptors.response.use(
        (res) => {
            const body = res.data;

            if (
                mode !== 'flat' &&
                body &&
                typeof body === 'object' &&
                !Array.isArray(body) &&
                'data' in body &&
                'trace_id' in body
            ) {
                return body.data;
            }

            return body;
        },
        (err: AxiosError) => {
            if (!err.response) {
                showConnectionErrorNotification();
                return Promise.reject(createNetworkError());
            }

            const status = err.response.status;

            if (status === 503) {
                if (mode === 'flat') {
                    store.dispatch(setMaintenance(true));
                }
                showMaintenanceNotification();
            }

            const data = err.response?.data as { message?: string; error?: string } | undefined;
            const code = getErrorCode(data);
            if (
                mode !== 'flat' &&
                isSessionRejected(status, data) &&
                storage.get<string>('_zugang', '')
            ) {
                sessionExpiredHandler?.();
            }
            if (
                code === 'project_access_denied' ||
                (code === 'project_not_found' && err.config?.headers?.[PROJECT_HEADER])
            ) {
                projectErrorHandler?.();
            }

            if (data?.message) {
                return Promise.reject({ ...data, status });
            }
            if (data?.error) {
                return Promise.reject({ message: data.error, code, status });
            }

            if (err.message) {
                return Promise.reject({
                    message: err.message,
                    status,
                });
            }
            return Promise.reject({
                message: 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้ในขณะนี้',
                status,
            });
        }
    );

    return client;
}

const httpClient = attachInterceptors(
    axios.create({
        baseURL: API_ENDPOINT,
        withCredentials: true,
    }),
    'flat',
    true
);

export const kuferClient = attachInterceptors(
    axios.create({
        baseURL: KUFER_URL,
        withCredentials: false,
    }),
    'envelope',
    true
);

export const sammelnClient = attachInterceptors(
    axios.create({
        baseURL: SAMMELN_URL,
        withCredentials: false,
    }),
    'envelope-with-user-header'
);

export default httpClient;
